import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// --- DATA ---
const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const PHONICS = {
  A: "a as in apple",
  B: "b as in ball",
  C: "c as in cat",
  D: "d as in dog",
  E: "e as in egg",
  F: "f as in fish",
  G: "g as in goat",
  H: "h as in hat",
  I: "i as in ice",
  J: "j as in jug",
  K: "k as in kite",
  L: "l as in lion",
  M: "m as in monkey",
  N: "n as in nest",
  O: "o as in orange",
  P: "p as in parrot",
  Q: "qu as in queen",
  R: "r as in rabbit",
  S: "s as in sun",
  T: "t as in tiger",
  U: "u as in umbrella",
  V: "v as in van",
  W: "w as in whale",
  X: "x as in X-mas ",
  Y: "y as in yak",
  Z: "z as in zebra",
};

const LETTER_COLORS = {
  A: "#FF6B6B",
  B: "#4ECDC4",
  C: "#FFD166",
  D: "#06D6A0",
  E: "#118AB2",
  F: "#073B4C",
  G: "#EF476F",
  H: "#7209B7",
  I: "#F72585",
  J: "#3A86FF",
  K: "#FB5607",
  L: "#8338EC",
  M: "#3A0CA3",
  N: "#4CC9F0",
  O: "#F9C74F",
  P: "#90BE6D",
  Q: "#F8961E",
  R: "#43AA8B",
  S: "#577590",
  T: "#277DA1",
  U: "#F9844A",
  V: "#9CEC5B",
  W: "#5B8C5A",
  X: "#5C4B51",
  Y: "#8AC926",
  Z: "#1982C4",
};

const LETTER_ANIMALS = {
  A: "🍎",
  B: "⚽",
  C: "🐱",
  D: "🐶",
  E: "🥚",
  F: "🐟",
  G: "🐐",
  H: "🎩",
  I: "🧊",
  J: "🏺",
  K: "🪁",
  L: "🦁",
  M: "🐒",
  N: "🪺",
  O: "🍊",
  P: "🦜",
  Q: "👑",
  R: "🐇",
  S: "☀️",
  T: "🐯",
  U: "☂️",
  V: "🚐",
  W: "🐋",
  X: "🎄",
  Y: "🐂",
  Z: "🦓",
};

// Level definitions
const LEVELS = [
  {
    id: 1,
    name: "Level 1",
    letters: LETTERS.slice(0, 9),
    description: "A to I",
    unlocked: true,
  },
  {
    id: 2,
    name: "Level 2",
    letters: LETTERS.slice(9, 18),
    description: "J to R",
    unlocked: false,
  },
  {
    id: 3,
    name: "Level 3",
    letters: LETTERS.slice(18, 26),
    description: "S to Z",
    unlocked: false,
  },
  {
    id: 4,
    name: "Challenge",
    letters: [],
    description: "15 Random Letters",
    unlocked: false,
  },
];

// --- UTILS ---
function shuffle(array) {
  const a = [...array];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function getRandomLetters(count) {
  return shuffle(LETTERS).slice(0, count);
}

function useSpeech(enabled) {
  const indianVoice = useRef(null);

  useEffect(() => {
    const findIndianVoice = () => {
      if (!window.speechSynthesis) {
        console.warn("Speech synthesis not supported");
        return;
      }

      const voices = window.speechSynthesis.getVoices();
      const indianVoices = voices.filter(
        (voice) =>
          voice.lang.includes("en-IN") ||
          voice.name.includes("India") ||
          voice.name.includes("Priya") ||
          voice.name.includes("Ravi") ||
          voice.name.includes("Hindi")
      );

      if (indianVoices.length > 0) {
        indianVoice.current = indianVoices[0];
      } else if (voices.length > 0) {
        indianVoice.current =
          voices.find((voice) => voice.lang.includes("en")) || voices[0];
      }
    };

    findIndianVoice();

    if (
      window.speechSynthesis &&
      window.speechSynthesis.onvoiceschanged !== undefined
    ) {
      window.speechSynthesis.onvoiceschanged = findIndianVoice;
    }
  }, []);

  return (text) => {
    if (!enabled || !indianVoice.current || !window.speechSynthesis) return;

    try {
      window.speechSynthesis.cancel();
      const msg = new SpeechSynthesisUtterance(text);
      msg.voice = indianVoice.current;
      msg.rate = 0.9;
      msg.pitch = 1.0;
      msg.lang = "en-IN";
      window.speechSynthesis.speak(msg);
    } catch (error) {
      console.error("Speech synthesis error:", error);
    }
  };
}

// --- CONFETTI ---
const Confetti = () => (
  <div className="confetti-container">
    {[...Array(40)].map((_, i) => (
      <div
        key={i}
        className="confetti"
        style={{
          left: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 2}s`,
          backgroundColor: `hsl(${Math.random() * 360}, 100%, 67%)`,
          width: `${8 + Math.random() * 8}px`,
          height: `${8 + Math.random() * 8}px`,
        }}
      />
    ))}
  </div>
);

// --- LEVEL COMPLETION ANIMATION ---
const LevelCompleteAnimation = () => (
  <div className="level-complete-animation">
    <div className="firework"></div>
    <div className="firework"></div>
    <div className="firework"></div>
  </div>
);

// --- WRONG ANSWER ANIMATION ---
const WrongAnswerAnimation = ({ show, onComplete }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        onComplete();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [show, onComplete]);

  if (!show) return null;

  return (
    <motion.div
      className="wrong-answer-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="wrong-answer-content"
        initial={{ scale: 0.5, y: -50 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.5, y: -50 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
      >
        <motion.div
          className="elephant-shake"
          animate={{ rotate: [-5, 5, -5, 5, 0] }}
          transition={{ duration: 0.5, repeat: 2 }}
        >
          🐘
        </motion.div>
        <motion.div
          className="wrong-message"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3 }}
        >
          Try Again!
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

// --- LEVEL MODAL ---
function LevelModal({
  isOpen,
  onClose,
  currentLevel,
  onLevelChange,
  completedLevels,
}) {
  if (!isOpen) return null;

  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="modal-content level-modal"
        initial={{ scale: 0.8, y: 50 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.8, y: 50 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>Select Level</h3>
          <button className="modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-levels">
          {LEVELS.map((level) => {
            const isCompleted = completedLevels.includes(level.id);

            return (
              <motion.button
                key={level.id}
                onClick={() => {
                  if (level.unlocked) {
                    onLevelChange(level.id);
                    onClose();
                  }
                }}
                disabled={!level.unlocked}
                className={`modal-level-btn ${
                  currentLevel === level.id ? "active" : ""
                } ${isCompleted ? "completed" : ""} ${
                  !level.unlocked ? "locked" : ""
                }`}
                whileHover={{ scale: level.unlocked ? 1.05 : 1 }}
                whileTap={{ scale: level.unlocked ? 0.95 : 1 }}
              >
                <span className="modal-level-name">{level.name}</span>
                <span className="modal-level-desc">{level.description}</span>
                {isCompleted && <span className="modal-level-check">✓</span>}
                {!level.unlocked && (
                  <span className="modal-level-lock">🔒</span>
                )}
              </motion.button>
            );
          })}
        </div>
      </motion.div>
    </motion.div>
  );
}

// --- SETTINGS MODAL ---
function SettingsModal({ isOpen, onClose, onResetLevel, voiceOn, setVoiceOn }) {
  if (!isOpen) return null;

  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="modal-content settings-modal"
        initial={{ scale: 0.8, y: 50 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.8, y: 50 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>Settings</h3>
          <button className="modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="settings-options">
          <motion.button
            className="settings-btn"
            onClick={onResetLevel}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="settings-icon">🔄</span>
            Reset Level
          </motion.button>

          <motion.button
            className={`settings-btn ${voiceOn ? "active" : ""}`}
            onClick={() => setVoiceOn(!voiceOn)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="settings-icon">{voiceOn ? "🔊" : "🔇"}</span>
            Sound {voiceOn ? "On" : "Off"}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// --- MAIN COMPONENT ---
export default function AlphabetPuzzleGame() {
  const [currentLevel, setCurrentLevel] = useState(1);
  const [completedLevels, setCompletedLevels] = useState([]);
  const [attempts, setAttempts] = useState(0);
  const [correctAttempts, setCorrectAttempts] = useState(0);
  const [levelLetters, setLevelLetters] = useState([]);
  const [tiles, setTiles] = useState([]);
  const [placed, setPlaced] = useState({});
  const [voiceOn, setVoiceOn] = useState(true);
  const [levelCompleted, setLevelCompleted] = useState(false);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  const [draggedLetter, setDraggedLetter] = useState(null);
  const [showWrongAnimation, setShowWrongAnimation] = useState(false);
  const [isLevelActive, setIsLevelActive] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showLevelModal, setShowLevelModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const speak = useSpeech(voiceOn);

  // Unlock next level when completing current one
  useEffect(() => {
    if (completedLevels.length > 0) {
      const nextLevel = LEVELS.find((l) => l.id === currentLevel + 1);
      if (nextLevel) {
        nextLevel.unlocked = true;
      }
    }
  }, [completedLevels, currentLevel]);

  // Calculate accuracy
  const accuracy = useMemo(() => {
    if (attempts === 0) return 100;
    return Math.round((correctAttempts / attempts) * 100);
  }, [attempts, correctAttempts]);

  // Initialize level
  useEffect(() => {
    let letters;
    if (currentLevel === 4) {
      letters = getRandomLetters(15);
    } else {
      letters = LEVELS.find((l) => l.id === currentLevel).letters;
    }

    setLevelLetters(letters);
    setTiles(shuffle(letters));
    setPlaced({});
    setLevelCompleted(false);
    setIsLevelActive(true);
    setIsTransitioning(false);
    setShowHint(false);

    // Auto-hide hint after 5 seconds
    const hintTimer = setTimeout(() => {
      setShowHint(false);
    }, 5000);

    return () => clearTimeout(hintTimer);
  }, [currentLevel]);

  // ✅ Check level completion
  useEffect(() => {
    if (
      !isTransitioning && // guard: don't trigger during transitions
      Object.keys(placed).length === levelLetters.length &&
      levelLetters.length > 0 &&
      !levelCompleted
    ) {
      setLevelCompleted(true);
      setCelebrating(true);
      setIsLevelActive(false);

      // Add current level to completed levels if not already there
      setCompletedLevels((prev) => {
        if (!prev.includes(currentLevel)) {
          return [...prev, currentLevel];
        }
        return prev;
      });

      setTimeout(() => {
        setCelebrating(false);

        // Check if all levels are completed
        const newCompletedCount = completedLevels.includes(currentLevel)
          ? completedLevels.length
          : completedLevels.length + 1;

        if (newCompletedCount === LEVELS.length) {
          setGameCompleted(true);
        }
      }, 2000);
    }
  }, [
    placed,
    levelLetters,
    currentLevel,
    completedLevels,
    levelCompleted,
    isTransitioning,
  ]);

  // ✅ Handle level change
  const handleLevelChange = (levelId) => {
    if (isLevelActive || isTransitioning) return; // Prevent overlap

    setIsTransitioning(true); // mark transition
    setLevelCompleted(false); // close popup immediately
    setGameCompleted(false);
    setAttempts(0);
    setCorrectAttempts(0);
    setDraggedLetter(null);
    setShowWrongAnimation(false);

    setCurrentLevel(levelId); // trigger useEffect([currentLevel])
  };

  // ✅ Handle next level
  const handleNextLevel = () => {
    if (currentLevel < LEVELS.length && !isTransitioning) {
      handleLevelChange(currentLevel + 1); // direct call, no setTimeout
    }
  };

  // Handle restart game
  const handleRestartGame = () => {
    setCompletedLevels([]);
    setGameCompleted(false);
    handleLevelChange(1);
  };

  // Calculate grid columns based on letter count
  const getGridCols = () => {
    if (levelLetters.length <= 9) return 3;
    if (levelLetters.length <= 12) return 4;
    return 5;
  };

  // Show wrong answer animation
  const showWrongAnswer = () => {
    setShowWrongAnimation(true);
    if (voiceOn) {
      speak("Try again!");
    }
  };

  const hideWrongAnswer = () => {
    setShowWrongAnimation(false);
  };

  // --- Touch and Drag logic ---
  const onDragStart = (e, letter) => {
    e.dataTransfer.setData("text/plain", letter);
    setDraggedLetter(letter);
  };

  const onDragEnd = () => {
    setDraggedLetter(null);
  };

  const onDrop = (e, targetLetter) => {
    e.preventDefault();
    const from = e.dataTransfer.getData("text/plain");

    // Count attempt
    setAttempts((prev) => prev + 1);

    if (from === targetLetter && !placed[targetLetter]) {
      setCorrectAttempts((prev) => prev + 1);
      setPlaced((p) => ({ ...p, [targetLetter]: true }));
      setTiles((t) => t.filter((L) => L !== from));
      speak(PHONICS[targetLetter] || targetLetter);
    } else {
      // Show wrong answer animation
      showWrongAnswer();
    }
    setDraggedLetter(null);
  };

  const onDragOver = (e) => {
    e.preventDefault();
  };

  const onTouchStart = (letter) => {
    setDraggedLetter(letter);
  };

  const onTouchEnd = () => {
    setDraggedLetter(null);
  };

  const handleSlotClick = (targetLetter) => {
    if (draggedLetter && !placed[targetLetter]) {
      // Count attempt
      setAttempts((prev) => prev + 1);

      if (draggedLetter === targetLetter) {
        setCorrectAttempts((prev) => prev + 1);
        setPlaced((p) => ({ ...p, [targetLetter]: true }));
        setTiles((t) => t.filter((L) => L !== draggedLetter));
        speak(PHONICS[targetLetter] || targetLetter);
      } else {
        // Show wrong answer animation
        showWrongAnswer();
      }
      setDraggedLetter(null);
    }
  };

  const resetLevel = () => {
    setTiles(shuffle(levelLetters));
    setPlaced({});
    setLevelCompleted(false);
    setGameCompleted(false);
    setDraggedLetter(null);
    setAttempts(0);
    setCorrectAttempts(0);
    setShowWrongAnimation(false);
    setIsLevelActive(true);
    setShowSettingsModal(false);
  };

  const toggleHint = () => {
    setShowHint(!showHint);
  };

  return (
    <div className="game-container">
      <style>
        {`
          * {
            box-sizing: border-box;
            -webkit-tap-highlight-color: transparent;
          }

          body {
            margin: 0;
            padding: 0;
            overflow-x: hidden;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #f0f9ff;
          }

          .game-container {
            min-height: 100vh;
            background: linear-gradient(135deg, #a6e1ff 0%, #fbc2eb 100%);
            padding: 1rem;
            overflow-x: hidden;
            width: 100%;
            margin: 0;
            display: flex;
            flex-direction: column;
          }

          .game-header {
            text-align: center;
            margin-bottom: 1rem;
            max-width: 100%;
            flex-shrink: 0;
          }

          .game-title {
            font-size: 2.2rem;
            font-weight: bold;
            background: linear-gradient(45deg, #FF6B6B, #FFD166, #06D6A0, #118AB2);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            margin: 0 0 0.75rem 0;
            text-shadow: 2px 2px 4px rgba(0,0,0,0.1);
          }

          .header-controls {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 0.75rem;
            gap: 0.5rem;
          }

          .header-btn {
            padding: 0.7rem 1.2rem;
            border: none;
            border-radius: 16px;
            font-weight: bold;
            cursor: pointer;
            transition: all 0.2s;
            outline: none;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(255, 255, 255, 0.9);
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
            font-family: 'Comic Sans MS', 'Chalkboard SE', cursive, sans-serif;
          }

          .header-btn.level-btn {
            flex: 1;
            font-size: 1.5rem;
            padding: 0.7rem;
            background: linear-gradient(45deg, #FF9E6D, #FF6B6B);
            color: white;
          }

          .header-btn.settings-btn {
            flex: 1;
            font-size: 1.5rem;
            padding: 0.7rem;
            background: linear-gradient(45deg, #f7971e, #ffd200);
            color: white;
          }

          .header-btn.hint-btn {
            flex: 1;
            font-size: 1.5rem;
            padding: 0.7rem;
            background: linear-gradient(45deg, #3a0ca3, #7209b7, #4361ee);
            color: #333;
          }

          .stats {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 0.5rem;
            margin-bottom: 0.75rem;
          }

          .stat-item {
            background: rgba(255, 255, 255, 0.8);
            padding: 0.8rem;
            border-radius: 16px;
            color: #333;
            font-weight: bold;
            text-align: center;
            font-size: 0.9rem;
            display: flex;
            flex-direction: column;
            box-shadow: 0 4px 8px rgba(0,0,0,0.1);
            border: 2px solid rgba(255,255,255,0.5);
          }

          .stat-label {
            font-size: 0.75rem;
            opacity: 0.9;
            margin-bottom: 0.2rem;
            color: #666;
          }

          .stat-value {
            font-size: 1.1rem;
            color: #06D6A0;
          }

          .instruction {
            background: rgba(255, 255, 255, 0.9);
            padding: 1rem;
            border-radius: 16px;
            text-align: center;
            font-weight: bold;
            color: #333;
            margin-bottom: 0.75rem;
            border: 3px dashed #FFD166;
            max-width: 100%;
            font-size: 1rem;
            box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          }

          .hint-text {
            margin-top: 0.5rem;
            font-size: 0.9rem;
            color: #118AB2;
            font-style: italic;
          }

          .game-content {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            flex: 1;
            min-height: 0;
            width: 100%;
          }

          .grids-container {
            background: rgba(255, 255, 255, 0.3);
            border-radius: 20px;
            padding: 1.2rem;
            flex: 1;
            min-height: 0;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            border: 3px solid rgba(255,255,255,0.5);
            box-shadow: 0 8px 16px rgba(0,0,0,0.1);
          }

          .grid-title {
            color: #333;
            text-align: center;
            margin: 0 0 0.75rem 0;
            font-size: 1.2rem;
            font-weight: bold;
            background: rgba(255,255,255,0.7);
            padding: 0.5rem;
            border-radius: 12px;
          }

          .letter-grid {
            display: grid;
            gap: 0.6rem;
            grid-template-columns: repeat(${getGridCols()}, minmax(0, 1fr));
            justify-content: center;
            width: 100%;
            margin: 0 auto;
            flex: 1;
            min-height: 0;
            overflow-y: auto;
            padding: 0.5rem;
          }

          .slot-tile {
            background: rgba(255, 255, 255, 0.95);
            border: 3px solid #FFD166;
            border-radius: 16px;
            padding: 0.8rem;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 80px;
            cursor: pointer;
            transition: all 0.3s;
            position: relative;
            user-select: none;
            box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          }

          .slot-tile.placed {
            border-color: white;
            box-shadow: 0 0 15px rgba(255,255,255,0.6);
            transform: scale(1.03);
          }

          .slot-tile.drag-over {
            background: rgba(255, 209, 102, 0.3);
            border-color: #FF6B6B;
            transform: scale(1.05);
            box-shadow: 0 0 15px rgba(255,107,107,0.4);
          }

          .slot-animal {
            font-size: 2rem;
            transition: all 0.3s;
            filter: drop-shadow(2px 2px 2px rgba(0,0,0,0.2));
          }

          .slot-tile.placed .slot-animal {
            font-size: 1.5rem;
            margin-bottom: 0.3rem;
          }

          .slot-letter {
            font-size: 1.6rem;
            font-weight: bold;
            color: #333;
            height: 0;
            opacity: 0;
            transition: all 0.3s;
            text-shadow: 1px 1px 2px rgba(0,0,0,0.1);
          }

          .slot-tile.placed .slot-letter {
            height: auto;
            opacity: 1;
          }

          .tiles-section {
            background: rgba(255, 255, 255, 0.3);
            border-radius: 20px;
            padding: 1.2rem;
            border: 3px solid rgba(255,255,255,0.5);
            width: 100%;
            flex-shrink: 0;
            box-shadow: 0 8px 16px rgba(0,0,0,0.1);
          }

          .tiles-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 0.75rem;
            flex-wrap: wrap;
            gap: 0.5rem;
          }

          .tiles-title {
            color: #333;
            font-weight: bold;
            font-size: 1.2rem;
            margin: 0;
            background: rgba(255,255,255,0.7);
            padding: 0.4rem 0.8rem;
            border-radius: 12px;
          }

          .tiles-grid {
            display: flex;
            flex-wrap: wrap;
            gap: 0.6rem;
            justify-content: center;
            width: 100%;
            max-height: 300px;
            overflow-y: auto;
            padding: 0.5rem;
          }

          .draggable-tile {
            width: 60px;
            height: 60px;
            border-radius: 50%;
            border: none;
            font-size: 1.8rem;
            font-weight: bold;
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: grab;
            transition: all 0.2s;
            user-select: none;
            outline: none;
            box-shadow: 0 4px 8px rgba(0,0,0,0.2);
            font-family: 'Comic Sans MS', 'Chalkboard SE', cursive, sans-serif;
          }

          .draggable-tile:active {
            cursor: grabbing;
            transform: scale(1.1);
          }

          .draggable-tile.selected {
            transform: scale(1.2);
            box-shadow: 0 0 20px rgba(255,255,255,0.8);
            z-index: 10;
          }

          .empty-tiles {
            color: white;
            font-weight: bold;
            text-align: center;
            font-size: 1.2rem;
            padding: 0.5rem;
            width: 100%;
            text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
          }

          .confetti-container {
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            pointer-events: none;
            z-index: 100;
          }

          .confetti {
            position: absolute;
            border-radius: 50%;
            opacity: 0.85;
            animation: fall 4s linear infinite;
          }

          @keyframes fall {
            0% { transform: translateY(-80px) rotate(0deg); opacity: 1; }
            100% { transform: translateY(100vh) rotate(360deg); opacity: 0; }
          }

          .level-complete-animation {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 101;
          }

          .firework {
            position: absolute;
            width: 10px;
            height: 10px;
            border-radius: 50%;
            background: #ff0;
            animation: explode 1s ease-out forwards;
          }

          @keyframes explode {
            0% { transform: scale(1); opacity: 1; }
            100% { transform: scale(20); opacity: 0; }
          }

          .wrong-answer-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.3);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 99;
            pointer-events: none;
          }

          .wrong-answer-content {
            background: white;
            border-radius: 20px;
            padding: 1.5rem;
            text-align: center;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
            border: 3px solid #ff6b6b;
          }

          .elephant-shake {
            font-size: 4rem;
            margin-bottom: 0.5rem;
            display: block;
          }

          .wrong-message {
            font-size: 1.2rem;
            font-weight: bold;
            color: #ff6b6b;
          }

          .completion-modal {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0,0,0,0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 1rem;
          }

          .completion-content {
            background: white;
            border-radius: 20px;
            padding: 1.5rem;
            text-align: center;
            max-width: 90%;
            width: 320px;
            border: 3px solid #FFD166;
            box-shadow: 0 10px 25px rgba(0,0,0,0.2);
          }

          .completion-emoji {
            font-size: 3rem;
            margin-bottom: 0.75rem;
          }

          .completion-title {
            color: #06D6A0;
            font-weight: bold;
            font-size: 1.5rem;
            margin-bottom: 0.5rem;
          }
          
          .completion-text {
            color: #333;
            margin-bottom: 1rem;
            font-size: 1rem;
          }

          .accuracy-display {
            font-size: 1.3rem;
            font-weight: bold;
            margin: 1rem 0;
            color: #06D6A0;
          }

          .modal-buttons {
            display: flex;
            gap: 0.5rem;
            justify-content: center;
            flex-wrap: wrap;
          }

          /* Modal styles */
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0,0,0,0.6);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 1rem;
          }

          .modal-content {
            background: white;
            border-radius: 20px;
            padding: 1.5rem;
            max-width: 90%;
            width: 350px;
            max-height: 80vh;
            overflow-y: auto;
            border: 3px solid #FFD166;
            box-shadow: 0 10px 25px rgba(0,0,0,0.2);
          }

          .settings-modal {
            width: 300px;
          }

          .level-modal {
            width: 320px;
          }

          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1rem;
            border-bottom: 2px dashed #FFD166;
            padding-bottom: 0.5rem;
          }

          .modal-header h3 {
            margin: 0;
            color: #333;
            font-size: 1.5rem;
          }

          .modal-close {
            background: none;
            border: none;
            font-size: 1.5rem;
            cursor: pointer;
            color: #666;
            padding: 0.2rem;
          }

          .modal-levels {
            display: flex;
            flex-direction: column;
            gap: 0.7rem;
          }

          .modal-level-btn {
            padding: 1rem;
            border: none;
            border-radius: 16px;
            font-weight: bold;
            cursor: pointer;
            transition: all 0.2s;
            outline: none;
            display: flex;
            flex-direction: column;
            align-items: center;
            position: relative;
            background: rgba(0,0,0,0.05);
            font-family: 'Comic Sans MS', 'Chalkboard SE', cursive, sans-serif;
          }

          .modal-level-btn.active {
            background: linear-gradient(45deg, #FF6B6B, #FF9E6D);
            color: white;
          }

          .modal-level-btn.completed {
            background: linear-gradient(45deg, #06D6A0, #4ECDC4);
            color: white;
          }

          .modal-level-btn.locked {
            background: rgba(128, 128, 128, 0.2);
            color: #999;
            cursor: not-allowed;
          }

          .modal-level-name {
            font-size: 1rem;
          }

          .modal-level-desc {
            font-size: 0.9rem;
          }

          .modal-level-check {
            position: absolute;
            top: -5px;
            right: -5px;
            background: #06D6A0;
            color: white;
            border-radius: 50%;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.9rem;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          }

          .modal-level-lock {
            position: absolute;
            top: -5px;
            right: -5px;
            font-size: 1.1rem;
            background: white;
            border-radius: 50%;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          }

          .settings-options {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }

          .settings-btn {
            padding: 1.2rem;
            border: none;
            border-radius: 16px;
            font-weight: bold;
            cursor: pointer;
            transition: all 0.2s;
            outline: none;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.7rem;
            background: rgba(0,0,0,0.05);
            font-size: 1.1rem;
            font-family: 'Comic Sans MS', 'Chalkboard SE', cursive, sans-serif;
          }

          .settings-btn.active {
            background: linear-gradient(45deg, #06D6A0, #4ECDC4);
            color: white;
          }

          .settings-icon {
            font-size: 1.5rem;
          }

          /* Bounce animation for tiles */
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% {transform: translateY(0);}
            40% {transform: translateY(-20px);}
            60% {transform: translateY(-10px);}
          }

          .bounce {
            animation: bounce 1s;
          }

          /* Mobile optimizations */
          @media (max-width: 768px) {
            .game-container {
              padding: 0.8rem;
            }

            .game-title {
              font-size: 1.8rem;
            }

            .header-controls {
              flex-wrap: wrap;
            }

            .header-btn {
              padding: 0.6rem 1rem;
              font-size: 0.9rem;
              border-radius: 14px;
              flex: 1;
              min-width: 80px;
            }

            .header-btn.settings-btn {
              font-size: 1.3rem;
              padding: 0.6rem;
            }

            .header-btn.hint-btn {
              font-size: 1.1rem;
              padding: 0.6rem;
            }

            .stats {
              grid-template-columns: repeat(2, 1fr);
              gap: 0.4rem;
            }

            .stat-item {
              padding: 0.6rem;
            }

            .stat-label {
              font-size: 0.7rem;
            }

            .stat-value {
              font-size: 1rem;
            }

            .instruction {
              font-size: 0.9rem;
              padding: 0.8rem;
            }

            .grids-container {
              padding: 1rem;
            }

            .letter-grid {
              gap: 0.5rem;
              grid-template-columns: repeat(auto-fit, minmax(70px, 1fr));
            }

            .slot-tile {
              min-height: 70px;
              padding: 0.6rem;
            }

            .slot-animal {
              font-size: 1.6rem;
            }

            .slot-tile.placed .slot-animal {
              font-size: 1.3rem;
            }

            .slot-letter {
              font-size: 1.4rem;
            }

            .tiles-section {
              padding: 1rem;
            }

            .tiles-grid {
              gap: 0.5rem;
              max-height: 160px;
            }

            .draggable-tile {
              width: 50px;
              height: 50px;
              font-size: 1.5rem;
            }

            .modal-content {
              padding: 1.2rem;
            }

            .completion-content {
              padding: 1.2rem;
              width: 300px;
            }

            .completion-emoji {
              font-size: 2.5rem;
            }

            .completion-title {
              font-size: 1.3rem;
            }

            .completion-text {
              font-size: 0.9rem;
            }

            .elephant-shake {
              font-size: 3.5rem;
            }

            .wrong-message {
              font-size: 1.1rem;
            }
          }

          /* Extra small screens */
          @media (max-width: 480px) {
            .game-title {
              font-size: 1.6rem;
            }

            .header-btn {
              padding: 0.5rem 0.8rem;
              font-size: 0.8rem;
              min-width: 70px;
            }

            .header-btn.settings-btn {
              font-size: 1.2rem;
              padding: 0.5rem;
            }

            .header-btn.hint-btn {
              font-size: 1rem;
              padding: 0.5rem;
            }

            .stat-item {
              padding: 0.5rem;
            }

            .stat-label {
              font-size: 0.65rem;
            }

            .stat-value {
              font-size: 0.9rem;
            }

            .instruction {
              font-size: 0.85rem;
              padding: 0.7rem;
            }

            .letter-grid {
              grid-template-columns: repeat(auto-fit, minmax(60px, 1fr));
              gap: 0.4rem;
            }

            .slot-tile {
              min-height: 60px;
              padding: 0.5rem;
            }

            .slot-animal {
              font-size: 1.4rem;
            }

            .slot-tile.placed .slot-animal {
              font-size: 1.1rem;
            }

            .slot-letter {
              font-size: 1.2rem;
            }

            .draggable-tile {
              width: 45px;
              height: 45px;
              font-size: 1.3rem;
            }

            .elephant-shake {
              font-size: 3rem;
            }

            .wrong-answer-content {
              padding: 1.2rem;
            }
          }

          /* Level 4 specific adjustments */
          .level-4-grid {
            grid-template-columns: repeat(auto-fit, minmax(55px, 1fr)) !important;
            gap: 0.4rem !important;
          }

          .level-4-slot {
            min-height: 55px !important;
            padding: 0.4rem !important;
          }

          .level-4-animal {
            font-size: 1.2rem !important;
          }

          .level-4-letter {
            font-size: 1rem !important;
          }

          .level-4-draggable {
            width: 40px !important;
            height: 40px !important;
            font-size: 1.1rem !important;
          }

          /* Desktop layout */
          @media (min-width: 769px) {
            .game-content {
              flex-direction: row;
              max-height: calc(100vh - 200px);
            }

            .grids-container {
              flex: 2;
            }

            .tiles-section {
              flex: 1;
              max-width: 320px;
            }
          }

          /* Large screens */
          @media (min-width: 1200px) {
            .game-container {
              padding: 1.5rem;
            }
            
            .letter-grid {
              gap: 0.8rem;
            }
            
            .slot-tile {
              min-height: 90px;
            }
            
            .slot-animal {
              font-size: 2.2rem;
            }
            
            .slot-tile.placed .slot-animal {
              font-size: 1.6rem;
            }
            
            .slot-letter {
              font-size: 1.8rem;
            }
            
            .draggable-tile {
              width: 65px;
              height: 65px;
              font-size: 1.9rem;
            }
          }

          /* Prevent zoom on iOS input focus */
          @media screen and (max-width: 768px) {
            select, input, button {
              font-size: 16px !important;
            }
          }
        `}
      </style>

      <AnimatePresence>
        {showWrongAnimation && (
          <WrongAnswerAnimation
            show={showWrongAnimation}
            onComplete={hideWrongAnswer}
          />
        )}
      </AnimatePresence>

      {celebrating && <Confetti />}
      {levelCompleted && <LevelCompleteAnimation />}

      <LevelModal
        isOpen={showLevelModal}
        onClose={() => setShowLevelModal(false)}
        currentLevel={currentLevel}
        onLevelChange={handleLevelChange}
        completedLevels={completedLevels}
      />

      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        onResetLevel={resetLevel}
        voiceOn={voiceOn}
        setVoiceOn={setVoiceOn}
      />

      <div className="game-header">
        <h1 className="game-title">Alphabet Adventure 🎓</h1>

        <div className="header-controls">
          <motion.button
            onClick={() => setShowLevelModal(true)}
            className="header-btn level-btn"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Level: {currentLevel}/4
          </motion.button>
          <motion.button
            onClick={() => setShowSettingsModal(true)}
            className="header-btn settings-btn"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            ⚙️
          </motion.button>
          <motion.button
            onClick={toggleHint}
            className="header-btn hint-btn"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            💡
          </motion.button>
        </div>

        <div className="stats">
          <div className="stat-item">
            <span className="stat-label">Accuracy</span>
            <span className="stat-value">{accuracy}%</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Completed</span>
            <span className="stat-value">{completedLevels.length}/4</span>
          </div>
        </div>

        <div className="instruction">
          {draggedLetter
            ? `Selected: ${draggedLetter} - Tap the matching slot!`
            : "Select a letter from the bottom, then drop it on the matching slot!"}
          {showHint && (
            <div className="hint-text">
              Match the letter to the picture that starts with that sound!
            </div>
          )}
        </div>
      </div>

      <div className="game-content">
        <div className="grids-container">
          <h3 className="grid-title">
            Match Letters to Pictures -{" "}
            {LEVELS.find((l) => l.id === currentLevel).description}
          </h3>
          <div
            className={`letter-grid ${
              currentLevel === 4 ? "level-4-grid" : ""
            }`}
          >
            {levelLetters.map((L) => (
              <motion.div
                key={L}
                onDragOver={onDragOver}
                onDrop={(e) => onDrop(e, L)}
                onClick={() => handleSlotClick(L)}
                className={`slot-tile ${placed[L] ? "placed" : ""} ${
                  draggedLetter === L ? "drag-over" : ""
                } ${currentLevel === 4 ? "level-4-slot" : ""}`}
                style={{
                  borderColor: placed[L] ? LETTER_COLORS[L] : "#FFD166",
                  background: placed[L]
                    ? `linear-gradient(45deg, ${LETTER_COLORS[L]}40, ${LETTER_COLORS[L]}20)`
                    : "rgba(255, 255, 255, 0.95)",
                }}
                whileHover={{ scale: placed[L] ? 1.02 : 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <span
                  className={`slot-animal ${
                    currentLevel === 4 ? "level-4-animal" : ""
                  }`}
                >
                  {LETTER_ANIMALS[L]}
                </span>
                {placed[L] && (
                  <span
                    className={`slot-letter ${
                      currentLevel === 4 ? "level-4-letter" : ""
                    }`}
                  >
                    {L}
                  </span>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        <div className="tiles-section">
          <div className="tiles-header">
            <h3 className="tiles-title">Drag Letters</h3>
            <motion.button
              onClick={() => setTiles(shuffle(tiles))}
              className="header-btn"
              style={{ padding: "0.5rem 0.8rem", fontSize: "0.9rem" }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              🔀 Shuffle
            </motion.button>
          </div>

          <div className="tiles-grid">
            {tiles.map((L) => (
              <motion.button
                key={L}
                draggable
                onDragStart={(e) => onDragStart(e, L)}
                onDragEnd={onDragEnd}
                onTouchStart={() => onTouchStart(L)}
                onTouchEnd={onTouchEnd}
                onClick={() => setDraggedLetter(draggedLetter === L ? null : L)}
                className={`draggable-tile ${
                  draggedLetter === L ? "selected" : ""
                } ${currentLevel === 4 ? "level-4-draggable" : ""}`}
                style={{ background: LETTER_COLORS[L] }}
                whileHover={{ scale: 1.1, rotate: 5 }}
                whileTap={{ scale: 0.95 }}
                initial={{ scale: 0 }}
                animate={{ scale: draggedLetter === L ? 1.2 : 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              >
                {L}
              </motion.button>
            ))}

            {tiles.length === 0 && (
              <motion.div
                className="empty-tiles"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
              >
                🎉 All letters placed!
              </motion.div>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {levelCompleted && !gameCompleted && (
          <motion.div
            className="completion-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="completion-content"
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
            >
              <div className="completion-emoji">🎉</div>
              <h4 className="completion-title">
                Level {currentLevel} Complete!
              </h4>
              <div className="accuracy-display">Accuracy: {accuracy}%</div>
              <p className="completion-text">
                {currentLevel < LEVELS.length
                  ? "Ready for the next challenge?"
                  : "You've completed all levels!"}
              </p>
              <div className="modal-buttons">
                {currentLevel < LEVELS.length ? (
                  <motion.button
                    onClick={handleNextLevel}
                    className="header-btn"
                    disabled={isTransitioning}
                    style={{
                      width: "100%",
                      background: "linear-gradient(45deg, #FF6B6B, #FF9E6D)",
                      color: "white",
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {isTransitioning ? "Loading..." : "Next Level →"}
                  </motion.button>
                ) : (
                  <motion.button
                    onClick={handleRestartGame}
                    className="header-btn"
                    style={{
                      width: "100%",
                      background: "linear-gradient(45deg, #06D6A0, #4ECDC4)",
                      color: "white",
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Play Again
                  </motion.button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {gameCompleted && (
          <motion.div
            className="completion-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="completion-content"
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
            >
              <div className="completion-emoji">🏆🎓🌟</div>
              <h4 className="completion-title">Alphabet Master!</h4>
              <div className="accuracy-display">
                Overall Accuracy: {accuracy}%
              </div>
              <p className="completion-text">
                You've successfully completed all levels!
                <br />
                You're an alphabet expert!
              </p>
              <motion.button
                onClick={handleRestartGame}
                className="header-btn"
                style={{
                  width: "100%",
                  background: "linear-gradient(45deg, #06D6A0, #4ECDC4)",
                  color: "white",
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                🎮 Play Again
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
